import React, { useState, useEffect } from "react";
import axios, { AxiosError } from "axios";
import {
  Clock,
  Star,
  Crown,
  CheckCircle,
  CreditCard,
  Wallet,
  IndianRupee,
} from "lucide-react";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

interface FormData {
  name: string;
  email: string;
  phone: string;
  address: string;
  date: string;
  time: string;
  type: string;
  numberOfPeople: number;
}

export function BookDarshan() {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    phone: "",
    address: "",
    date: "",
    time: "",
    type: "regular",
    numberOfPeople: 1,
  });

  const [step, setStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [cvv, setCvv] = useState("");
  const [bookingResponse, setBookingResponse] = useState<any>(null);
  const [agreed, setAgreed] = useState(false);
  const [minDate, setMinDate] = useState("");
  const [activeDetail, setActiveDetail] = useState("");

  // Minimum selectable date is today
  useEffect(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    setMinDate(`${yyyy}-${mm}-${dd}`);
  }, []);

  // Darshan type details with pricing and benefits
  const darshanDetails = {
    regular: {
      price: "₹500/person",
      description: "Standard darshan experience with access to main temple rituals.",
      benefits: "Queue-based entry, basic facilities",
    },
    vip: {
      price: "₹1,000/person",
      description: "Priority darshan with shorter wait times and special seating.",
      benefits: "Dedicated queue, air-conditioned waiting area",
    },
    special: {
      price: "₹2,000/person",
      description: "Premium darshan including special pooja and personalized blessings.",
      benefits: "VIP entry, guided tour, commemorative certificate",
    },
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "numberOfPeople" ? parseInt(value) || 1 : value,
    }));
  };

  // Toggle darshan detail visibility
  const toggleDetails = (type: string) => {
    setActiveDetail((prev) => (prev === type ? "" : type));
  };

  // Calculate total cost based on type and number of people
  const calculateTotalCost = () => {
    switch (formData.type) {
      case "special":
        return 2000 * formData.numberOfPeople;
      case "vip":
        return 1000 * formData.numberOfPeople;
      case "regular":
        return 500 * formData.numberOfPeople;
      default:
        return 0;
    }
  };

  // Validate fields before proceeding or submitting
  const validateStep = () => {
    switch (step) {
      case 0:
        if (!agreed) {
          setError("You must agree to the booking instructions.");
          return false;
        }
        break;
      case 1:
        if (
          !formData.name.trim() ||
          !formData.email.trim() ||
          !formData.phone.trim() ||
          !formData.address.trim() ||
          !formData.date.trim() ||
          !formData.time.trim()
        ) {
          setError("Please fill in all fields.");
          return false;
        }
        // Validate email format
        if (!/\S+@\S+\.\S+/.test(formData.email)) {
          setError("Please enter a valid email address.");
          return false;
        }
        // Validate phone 10 digits
        if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ""))) {
          setError("Please enter a valid 10-digit phone number.");
          return false;
        }
        // Validate date is today or future
        const selectedDate = new Date(formData.date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (selectedDate < today) {
          setError("Booking date cannot be in the past.");
          return false;
        }
        break;
      case 2:
        if (!paymentMethod) {
          setError("Please select a payment method.");
          return false;
        }
        if (
          paymentMethod === "card" &&
          (!cardNumber.trim() || !expiryDate.trim() || !cvv.trim())
        ) {
          setError("Please fill in all card details.");
          return false;
        }
        break;
    }
    setError("");
    return true;
  };

  // Handle submitting the form at each step
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep()) return;

    if (step < 2) {
      setStep((s) => s + 1);
      return;
    }

    // Final submission (step 2)
    setIsLoading(true);
    setError("");
    setSuccessMessage("");
    try {
      // Submit booking API call
      const dataToSubmit = {
        ...formData,
        numberOfPeople: formData.numberOfPeople,
      };
      const response = await axios.post(`${API_URL}/api/darshan/book`, dataToSubmit, {
        headers: { "Content-Type": "application/json" },
      });

      if (response.data?.success) {
        setBookingResponse(response.data);
        setIsSuccess(true);
        setSuccessMessage(
          "Darshan booked successfully! A confirmation email has been sent to your email address with all booking details."
        );
        setStep(3); // Go to statement page

        // Auto-clear form data if you want here
        // setFormData({...});
      } else {
        setError(response.data?.message || "Something went wrong.");
      }
    } catch (err) {
      const axiosError = err as AxiosError<{ message: string }>;
      setError(
        axiosError.response?.data?.message ||
          "Error submitting form. Please try again later."
      );
      console.error("Booking Error:", axiosError);
    } finally {
      setIsLoading(false);
    }
  };

  // Reset form for new booking
  const handleNewBooking = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      address: "",
      date: "",
      time: "",
      type: "regular",
      numberOfPeople: 1,
    });
    setStep(0);
    setError("");
    setSuccessMessage("");
    setIsSuccess(false);
    setPaymentMethod("");
    setCardNumber("");
    setExpiryDate("");
    setCvv("");
    setBookingResponse(null);
    setAgreed(false);
  };

  // Payment method icons map
  const paymentMethods = [
    { id: "card", icon: CreditCard, label: "Credit/Debit Card" },
    { id: "upi", icon: Wallet, label: "UPI" },
    { id: "cash", icon: IndianRupee, label: "Cash at Desk" },
  ];

  if (step === 3 && bookingResponse) {
    // Statement page
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white py-12">
        <div className="max-w-4xl mx-auto p-8 bg-white shadow rounded-3xl border border-amber-300">
          <div className="text-center">
            <CheckCircle className="mx-auto h-16 w-16 text-green-600" />
            <h1 className="text-4xl font-extrabold mt-4 mb-2">Booking Confirmed!</h1>
            <p className="text-gray-700 mb-6">
              Thank you for booking your darshan slot. A confirmation email with
              the booking details has been sent to your email.
            </p>
          </div>

          <div className="border-t border-amber-300 pt-6 mt-6">
            <h2 className="font-semibold text-2xl mb-4 text-gray-900">Booking Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
              <p>
                <span className="font-semibold">Booking ID:</span>{" "}
                {bookingResponse.id || bookingResponse.bookingId || "N/A"}
              </p>
              <p>
                <span className="font-semibold">Name:</span> {formData.name}
              </p>
              <p>
                <span className="font-semibold">Email:</span> {formData.email}
              </p>
              <p>
                <span className="font-semibold">Phone:</span> {formData.phone}
              </p>
              <p>
                <span className="font-semibold">Address:</span> {formData.address}
              </p>
              <p>
                <span className="font-semibold">Date:</span> {formData.date}
              </p>
              <p>
                <span className="font-semibold">Time:</span> {formData.time}
              </p>
              <p>
                <span className="font-semibold">Darshan Type:</span>{" "}
                {formData.type.charAt(0).toUpperCase() + formData.type.slice(1)}
              </p>
              <p>
                <span className="font-semibold">Number of People:</span> {formData.numberOfPeople}
              </p>
              <p className="text-amber-600 font-bold text-lg mt-2">
                Total Paid: ₹{calculateTotalCost()}
              </p>
            </div>
          </div>

          <button
            onClick={handleNewBooking}
            className="mt-8 w-full rounded-xl bg-gradient-to-r from-orange-600 to-yellow-500 py-3 text-white font-semibold shadow hover:from-orange-700 hover:to-yellow-600 transition"
          >
            Book Another Darshan
          </button>
        </div>
      </div>
    );
  }

  return (
    <main className="bg-gradient-to-b from-orange-50 to-white py-12 min-h-screen">
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-4xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-yellow-500 mb-6">
          Book a Darshan
        </h1>

        {/* Step 0: Instructions & Agreement */}
        {step === 0 && (
          <form onSubmit={handleSubmit} className="bg-white p-8 rounded-3xl shadow-lg space-y-6 border border-amber-300">
            <h2 className="text-3xl font-semibold text-gray-900 mb-4">Booking Instructions</h2>
            <div className="bg-amber-50 p-6 rounded-xl text-gray-700">
              <p>Please read the instructions and agree before proceeding:</p>
              <ul className="list-disc pl-5 mt-3 space-y-1">
                <li>Booking date must be today or in the future.</li>
                <li>Ensure valid contact information is provided.</li>
                <li>Provide accurate number of people.</li>
                <li>Payment is to be made as per selected method.</li>
              </ul>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="agree"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="h-5 w-5 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
              />
              <label htmlFor="agree" className="ml-2 text-gray-700">
                I agree to the booking instructions and terms.
              </label>
            </div>
            {error && <p className="text-red-600 font-medium">{error}</p>}
            <button
              type="submit"
              disabled={!agreed || isLoading}
              className={`w-full rounded-xl py-3 text-white font-semibold shadow-md transition ${
                agreed
                  ? "bg-gradient-to-r from-orange-600 to-yellow-500 hover:from-orange-700 hover:to-yellow-600"
                  : "bg-gray-400 cursor-not-allowed"
              }`}
            >
              Proceed to Book
            </button>
          </form>
        )}

        {/* Step 1: Booking Details */}
        {step === 1 && (
          <form onSubmit={handleSubmit} className="bg-white p-8 rounded-3xl shadow-lg space-y-6 border border-amber-300">
            <h2 className="text-3xl font-semibold text-gray-900 mb-6">Enter Your Details</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Darshan Type Selection */}
              <div className="col-span-2 space-y-4">
                <p className="font-semibold text-gray-900 mb-2">Select Darshan Type</p>
                <div className="flex space-x-4">
                  {(["regular", "vip", "special"] as const).map((type) => (
                    <div
                      key={type}
                      className={`cursor-pointer rounded-xl border-2 p-4 shadow-md flex-grow text-center transition ${
                        formData.type === type ? "border-amber-600 bg-amber-50" : "border-gray-300"
                      }`}
                      onClick={() => setFormData({ ...formData, type })}
                    >
                      <p className="font-semibold text-gray-900">{type.charAt(0).toUpperCase() + type.slice(1)}</p>
                      <p className="text-sm text-gray-600">{darshanDetails[type].description}</p>
                      <p className="mt-1 font-semibold text-amber-600">{darshanDetails[type].price}</p>
                    </div>
                  ))}
                </div>
              </div>

              <label className="block">
                <span className="text-gray-700 font-medium">Full Name</span>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={isLoading}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500"
                />
              </label>

              <label className="block">
                <span className="text-gray-700 font-medium">Email</span>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={isLoading}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500"
                />
              </label>

              <label className="block">
                <span className="text-gray-700 font-medium">Phone Number</span>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={isLoading}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500"
                  maxLength={10}
                />
              </label>

              <label className="block">
                <span className="text-gray-700 font-medium">Address</span>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  disabled={isLoading}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500"
                />
              </label>

              <label className="block">
                <span className="text-gray-700 font-medium">Date</span>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  disabled={isLoading}
                  required
                  min={minDate}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500"
                />
              </label>

              <label className="block">
                <span className="text-gray-700 font-medium">Time</span>
                <input
                  type="time"
                  name="time"
                  value={formData.time}
                  onChange={handleChange}
                  disabled={isLoading}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500"
                />
              </label>

              <label className="block">
                <span className="text-gray-700 font-medium">Number of People</span>
                <input
                  type="number"
                  min={1}
                  name="numberOfPeople"
                  value={formData.numberOfPeople}
                  onChange={handleChange}
                  disabled={isLoading}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500"
                />
              </label>
            </div>
            {error && <p className="text-red-600 font-medium mt-2">{error}</p>}

            <div className="flex space-x-4 mt-6">
              <button
                type="button"
                onClick={() => setStep(0)}
                disabled={isLoading}
                className="flex-grow rounded-md border border-gray-300 py-3 font-semibold text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-grow rounded-md bg-gradient-to-r from-orange-600 to-yellow-500 py-3 font-semibold text-white shadow hover:from-orange-700 hover:to-yellow-600 transition disabled:opacity-50"
              >
                Next: Payment
              </button>
            </div>
          </form>
        )}

        {/* Step 2: Payment Selection */}
        {step === 2 && (
          <form onSubmit={handleSubmit} className="bg-white p-8 rounded-3xl shadow-lg space-y-6 border border-amber-300">
            <h2 className="text-3xl font-semibold text-gray-900 mb-6">Select Payment Method</h2>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {paymentMethods.map((method) => (
                <div
                  key={method.id}
                  className={`cursor-pointer flex items-center gap-3 rounded-xl border-2 p-6 shadow-md justify-center font-semibold transition ${
                    paymentMethod === method.id
                      ? "border-amber-600 bg-amber-50"
                      : "border-gray-300 hover:border-amber-300"
                  }`}
                  onClick={() => setPaymentMethod(method.id)}
                >
                  <method.icon className="text-amber-600" />
                  <span>{method.label}</span>
                </div>
              ))}
            </div>

            {paymentMethod === "card" && (
              <div className="space-y-4 mt-6">
                <label>
                  Card Number
                  <input
                    type="text"
                    maxLength={19}
                    value={cardNumber}
                    onChange={(e) => {
                      let val = e.target.value.replace(/\D/g, "");
                      val = val.match(/.{1,4}/g)?.join(" ") || val;
                      setCardNumber(val);
                    }}
                    required
                    disabled={isLoading}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500"
                    placeholder="1234 5678 9012 3456"
                  />
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <label>
                    Expiry Date (MM/YY)
                    <input
                      type="text"
                      maxLength={5}
                      value={expiryDate}
                      onChange={(e) => setExpiryDate(e.target.value)}
                      required
                      disabled={isLoading}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500"
                      placeholder="MM/YY"
                    />
                  </label>
                  <label>
                    CVV
                    <input
                      type="password"
                      maxLength={3}
                      value={cvv}
                      onChange={(e) => setCvv(e.target.value)}
                      required
                      disabled={isLoading}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500"
                      placeholder="123"
                    />
                  </label>
                </div>
              </div>
            )}

            {paymentMethod === "upi" && (
              <label className="block mt-6">
                UPI ID
                <input
                  type="text"
                  value={cardNumber} // reuse cardNumber state
                  onChange={(e) => setCardNumber(e.target.value)}
                  required
                  disabled={isLoading}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500"
                  placeholder="username@upi"
                />
              </label>
            )}

            {paymentMethod === "cash" && (
              <div className="rounded-xl bg-blue-50 p-6 mt-6">
                <p className="text-sm text-blue-700 font-medium">
                  Please pay at the temple counter at your visit. Your booking is pending admin confirmation.
                </p>
              </div>
            )}

            {error && <p className="text-red-600 font-medium">{error}</p>}

            <div className="flex space-x-4 mt-8">
              <button
                type="button"
                onClick={() => setStep(1)}
                disabled={isLoading}
                className="flex-grow rounded-md border border-gray-300 py-3 font-semibold text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
              >
                Back to Details
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-grow rounded-md bg-gradient-to-r from-orange-600 to-yellow-500 py-3 font-semibold text-white shadow hover:from-orange-700 hover:to-yellow-600 transition disabled:opacity-50"
              >
                {isLoading ? "Booking..." : "Confirm Booking"}
              </button>
            </div>
          </form>
        )}
      </div>
    </main>
  );
}

export default BookDarshan;
